import { keyframes, styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import { useAdminLandingView } from "../hooks/useAdminLandingView";
import {
  clearAdminViewSpace,
  useAdminSpaceView,
} from "../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../hooks/useAdminViewSubject";
import { useIsMobile } from "../hooks/useIsMobile";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { useQuery } from "../hooks/useQuery";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import {
  getAdminViewReturnUrl,
  isAdminViewUrl,
  markAdminViewUrl,
  rememberAdminViewReturnUrl,
} from "../lib/adminViewUrls";
import {
  makeUrl,
  routes,
  useCurrentLocation,
  useRouteMatch,
} from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { zIndexes } from "../lib/zIndexes";
import { AttentionStack } from "./AttentionStack";
import { Box } from "./Box";
import { Header } from "./Header";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { Navigation } from "./Navigation";
import { ProfileMenu } from "./ProfileMenu";
import { SearchButton } from "./SearchButton";
import { Text } from "./Text";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  background: var(--surface-secondary);
  width: 100%;
  height: 100%;
`;

const Layout = styled("div")`
  display: flex;
  flex: 1;
  overflow: hidden;
  padding-top: 1px;
  padding-bottom: 12px;
  padding-right: 12px;
  flex-direction: row;
  height: 100%;
  position: relative;
`;

const SIDEBAR_EXPANDED_WIDTH = 228;
const SIDEBAR_COLLAPSED_WIDTH = 12;
const SIDEBAR_TRANSITION_MS = 220;

const LeftSidebar = styled("div")`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  margin-top: -1px;
  width: ${SIDEBAR_EXPANDED_WIDTH}px;
  transition: width ${SIDEBAR_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
  &[data-collapsed="true"] {
    width: ${SIDEBAR_COLLAPSED_WIDTH}px;
  }
`;

const GutterHoverZone = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100%;
`;

const Separator = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--border-alpha);
  width: 16px;
  height: 16px;
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const TEMP_NAV_ENTER_MS = 180;
const TEMP_NAV_EXIT_MS = 110;

const TemporaryNavigationContainer = styled("div")`
  box-shadow: var(--elevation-high);
  position: absolute;
  z-index: ${zIndexes.navigation};
  top: 0;
  bottom: 0;
  background: var(--surface-secondary);
  display: flex;
  flex-direction: column;
  border-right: 0.5px solid var(--border-alpha);
  padding-bottom: 12px;
  &[data-state="entering"] {
    animation: ${slideIn} ${TEMP_NAV_ENTER_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  &[data-state="exiting"] {
    animation: ${slideOut} ${TEMP_NAV_EXIT_MS}ms cubic-bezier(0.5, 0, 0.9, 0.4)
      forwards;
  }
`;

const logoStyles = `
  color: var(--text-primary);
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 9999px;
  &:hover {
    background-color: var(--alpha4);
  }
`;

const LogoLink = styled(Link)`
  ${logoStyles}
`;

const LogoButton = styled("button")`
  ${logoStyles}
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
`;

const AdminBadge = styled("span")`
  margin-left: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  min-width: 0;
  max-width: 100%;
`;

const NavOverlayBackdrop = styled("div")`
  position: fixed;
  inset: 0;
  z-index: ${zIndexes.navigation - 1};
  background: rgba(13, 13, 23, 0.3);
`;

type Props = {
  children: React.ReactNode;
  afterPath?: React.ReactNode;
  middle?: React.ReactNode;
  permanentNavigation?: boolean;
  overrideLogo?: React.ReactNode;
  logoHref?: string;
  rightPrefix?: React.ReactNode;
  navigation?: React.ReactNode;
};

export function Backdrop({
  children,
  afterPath,
  middle,
  permanentNavigation: permanentNavigationProp,
  overrideLogo,
  logoHref,
  rightPrefix,
  navigation,
}: Props) {
  const isMobile = useIsMobile();
  const isNarrow = useIsNarrow();
  const [sidebarCollapsed] = useSidebarCollapsed();
  const effectivelyCollapsed = isNarrow || sidebarCollapsed;
  const [displayCollapsed, setDisplayCollapsed] =
    useState(effectivelyCollapsed);
  const [tempNavMounted, setTempNavMounted] = useState(false);
  useEffect(() => {
    if (effectivelyCollapsed && !displayCollapsed) {
      const t = setTimeout(
        () => setDisplayCollapsed(true),
        SIDEBAR_TRANSITION_MS
      );
      return () => clearTimeout(t);
    }
    if (!effectivelyCollapsed && displayCollapsed) {
      if (tempNavMounted) {
        const t = setTimeout(
          () => setDisplayCollapsed(false),
          SIDEBAR_TRANSITION_MS
        );
        return () => clearTimeout(t);
      }
      setDisplayCollapsed(false);
    }
  }, [effectivelyCollapsed, displayCollapsed, tempNavMounted]);
  const morphingFromTemp = !effectivelyCollapsed && displayCollapsed;
  const permanentNavigation = permanentNavigationProp && !displayCollapsed;
  const [user] = useQuery(queries.currentUser());
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const adminLanding = useAdminLandingView();
  const [adminUsers] = useQuery(adminSubject ? queries.allUsers() : null);
  const adminUser = adminSubject
    ? adminUsers?.find((u) => u.id === adminSubject)
    : undefined;
  const adminViewActive = !!adminSubject || !!adminSpace || adminLanding;
  const currentLocation = useCurrentLocation();
  useEffect(() => {
    if (adminViewActive) {
      markAdminViewUrl(currentLocation);
    } else {
      rememberAdminViewReturnUrl(currentLocation);
      clearAdminViewSpace();
    }
  }, [adminViewActive, currentLocation]);
  // A cold load of an admin URL renders before grants sync, so the effect
  // below can remember the admin URL itself as the return target — filter
  // those out here, where the URL has since been marked.
  const adminViewReturnUrl = getAdminViewReturnUrl();
  // From a workflow, back steps up to its space — still inside the admin
  // view — rather than exiting it.
  const [onSpaceWorkflow, spaceWorkflowMatch] = useRouteMatch(
    routes.space.workflow
  );
  const adminViewBackUrl = onSpaceWorkflow
    ? makeUrl(routes.space, {
        spaceId: (spaceWorkflowMatch as { spaceId: string }).spaceId,
      })
    : ((adminViewReturnUrl && !isAdminViewUrl(adminViewReturnUrl)
        ? adminViewReturnUrl
        : null) ??
      (adminSubject
        ? makeUrl(routes.settings.members.users)
        : makeUrl(routes.settings.spaces)));
  const [showingTemporaryNavigation, setShowingTemporaryNavigation] =
    useState(false);

  const defaultLogo = <Icon name="tines-mark" size={16} />;
  const navigationContent = navigation ?? <Navigation />;
  const customTemporaryNavigation = showingTemporaryNavigation && navigation;
  const logoContent =
    overrideLogo && (!showingTemporaryNavigation || customTemporaryNavigation)
      ? overrideLogo
      : defaultLogo;

  const onLogoHover = (hovering: boolean) => {
    if (isMobile) return;
    if (!permanentNavigation) {
      setShowingTemporaryNavigation(hovering);
    }
  };

  const gutterHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelGutterHover = () => {
    if (gutterHoverTimer.current) {
      clearTimeout(gutterHoverTimer.current);
      gutterHoverTimer.current = null;
    }
  };
  const scheduleGutterHover = () => {
    cancelGutterHover();
    gutterHoverTimer.current = setTimeout(() => {
      setShowingTemporaryNavigation(true);
    }, 250);
  };
  useEffect(() => {
    return () => {
      if (gutterHoverTimer.current) clearTimeout(gutterHoverTimer.current);
    };
  }, []);
  useEffect(() => {
    if (permanentNavigation) {
      setShowingTemporaryNavigation(false);
      if (gutterHoverTimer.current) {
        clearTimeout(gutterHoverTimer.current);
        gutterHoverTimer.current = null;
      }
    }
  }, [permanentNavigation]);

  const wantsTempNav =
    (showingTemporaryNavigation || morphingFromTemp) && !permanentNavigation;
  const [tempNavState, setTempNavState] = useState<"entering" | "exiting">(
    "entering"
  );
  useEffect(() => {
    if (wantsTempNav) {
      setTempNavMounted(true);
      setTempNavState("entering");
      return;
    }
    if (!tempNavMounted) return;
    if (permanentNavigation) {
      setTempNavMounted(false);
      return;
    }
    setTempNavState("exiting");
    const t = setTimeout(() => setTempNavMounted(false), TEMP_NAV_EXIT_MS);
    return () => clearTimeout(t);
  }, [wantsTempNav, tempNavMounted, permanentNavigation]);

  const toggleMobileNav = () => {
    setShowingTemporaryNavigation((prev) => !prev);
  };

  const mobileCanOpenNav = isMobile;

  return (
    <Container>
      <Header
        mobile={isMobile}
        logo={
          <div
            onMouseEnter={isMobile ? undefined : () => onLogoHover?.(true)}
            style={{
              position: "relative",
              zIndex: zIndexes.navigation + 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {mobileCanOpenNav ? (
              <LogoButton type="button" onClick={toggleMobileNav}>
                {logoContent}
              </LogoButton>
            ) : adminViewActive ? (
              <LogoLink to={adminViewBackUrl} aria-label="Back">
                <Icon name="arrow-left" size={16} />
              </LogoLink>
            ) : (
              <LogoLink to={logoHref ?? "/"}>{logoContent}</LogoLink>
            )}
          </div>
        }
        left={
          <div
            style={{
              position: customTemporaryNavigation ? "relative" : undefined,
              zIndex: customTemporaryNavigation
                ? zIndexes.navigation + 1
                : undefined,
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            {adminViewActive ? (
              <>
                {afterPath ?? (
                  <Box horizontal={4} vertical={8} center>
                    <Text variant="text-regular-md">Watchtower</Text>
                  </Box>
                )}
                {adminUser && (
                  <AdminBadge>
                    <Icon name="circle-user-round" size={10} />
                    <Text variant="text-small-md" ellipsis>
                      {userDisplayName(adminUser)}
                    </Text>
                  </AdminBadge>
                )}
                {!adminUser && adminSpace && !afterPath && (
                  <AdminBadge>
                    <Icon name="hash" size={10} />
                    <Text variant="text-small-md" ellipsis>
                      {adminSpace.name}
                    </Text>
                  </AdminBadge>
                )}
                {afterPath && (
                  <AdminBadge>
                    <Icon name="tower-control" size={10} />
                    <Text variant="text-small-md" ellipsis>
                      Watchtower
                    </Text>
                  </AdminBadge>
                )}
              </>
            ) : (
              afterPath
            )}
          </div>
        }
        middle={middle}
        right={
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {rightPrefix}
            {rightPrefix && !isMobile && <Separator>/</Separator>}
            {!isMobile && <SearchButton />}
            {!isMobile && <AttentionStack />}
            {user && <ProfileMenu user={user} />}
          </div>
        }
      />
      {permanentNavigationProp && !isMobile ? (
        <Layout>
          <LeftSidebar data-collapsed={effectivelyCollapsed}>
            {displayCollapsed ? (
              <GutterHoverZone
                onMouseEnter={scheduleGutterHover}
                onMouseLeave={cancelGutterHover}
              />
            ) : (
              navigationContent
            )}
          </LeftSidebar>
          {children}
        </Layout>
      ) : permanentNavigationProp && isMobile ? (
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            padding: "1px 8px 8px",
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {tempNavMounted && (
        <>
          {isMobile && tempNavState === "entering" && (
            <NavOverlayBackdrop
              onClick={() => setShowingTemporaryNavigation(false)}
            />
          )}
          <TemporaryNavigationContainer
            data-popover-host
            data-state={tempNavState}
            onMouseLeave={
              isMobile ? undefined : () => setShowingTemporaryNavigation(false)
            }
          >
            <div style={{ height: 56 }} />
            {navigationContent}
          </TemporaryNavigationContainer>
        </>
      )}
    </Container>
  );
}
